import React from 'react';
import {
  ArrowRight,
  BarChart3,
  FileText,
  Users,
  Wallet,
  Wrench,
} from 'lucide-react';
import { Button } from '../ui/button';

const ASSETS = {
  logo: '/images/checklist-result/logo.png',
  cubesGroup: '/images/checklist-result/cubes-group.png',
};

/** Макет Figma node 2318:1490 — статичные данные для вёрстки (без логики). */
export const CHECKLIST_RESULT_MOCK = {
  title: 'Поздравляем!',
  subtitle: 'Вы прошли чек-лист',
  score: 80,
  maxScore: 230,
  readinessLabel: 'Частично готовы',
  readinessEmoji: '⚠️',
  summary:
    'У вашей компании уже есть сильная база для внедрения AI, но несколько операционных направлений всё ещё требуют доработки перед масштабированием.',
  /** 427px / 619px в макете */
  progressPercent: 69,
  progressLabels: ['Не готовы', 'Частично готовы', 'Можно внедрять'],
  pillars: [
    { id: 'processes', label: 'Процессы', score: 20, progress: 50, Icon: FileText },
    { id: 'team', label: 'Команда', score: 32, progress: 64, Icon: Users },
    { id: 'tools', label: 'Инструменты', score: 30, progress: 100, Icon: Wrench },
    { id: 'content', label: 'Контент', score: 30, progress: 75, Icon: BarChart3 },
    { id: 'budget', label: 'Бюджет', score: 50, progress: 100, Icon: Wallet },
  ],
  ctaHeading: 'Хотите внедрить Gen AI в продажи?',
  ctaSubtext: 'Получите результаты и запишитесь на консультацию с экспертом',
  ctaLabel: 'Получить полные результаты и записаться',
};

function ReadinessBadge({ label, emoji, readyLabels = ['Можно внедрять'] }) {
  const isReady = readyLabels.includes(label);

  if (isReady) {
    return (
      <div
        className="inline-flex h-[37px] items-center gap-2 rounded-lg px-4"
        data-testid="readiness-badge-ready"
      >
        <span className="text-sm font-medium text-black" aria-hidden>
          {emoji}
        </span>
        <span className="text-sm font-medium tracking-[-0.15px] text-black">
          {label}
        </span>
      </div>
    );
  }

  return (
    <div className="inline-flex h-[37px] items-center gap-2 rounded-lg border border-[#E3AF20]/30 bg-[#FF9C61]/[0.13] px-4">
      <span className="text-sm font-medium text-[#E3AF20]" aria-hidden>
        {emoji}
      </span>
      <span className="text-sm font-medium tracking-[-0.15px] text-[#E3AF20]">{label}</span>
    </div>
  );
}

function PillarCard({ label, score, progress, Icon }) {
  return (
    <article
      className="relative min-w-0 overflow-hidden"
      aria-label={`${label}: ${score}`}
    >
      <div
        className="absolute inset-0 rounded-[14px] opacity-60 blur-md"
        style={{
          background:
            'linear-gradient(132deg, rgba(173, 70, 255, 0.05) 0%, rgba(43, 127, 255, 0.05) 100%)',
        }}
        aria-hidden
      />
      <div className="relative flex h-[121px] min-w-0 flex-col rounded-[14px] bg-white/50 p-3 shadow-sm sm:p-4">
        <Icon className="h-5 w-5 shrink-0 text-foreground" strokeWidth={1.75} aria-hidden />
        <p className="font-even-mono mt-2 text-[22px] leading-7 tracking-[-0.45px] text-foreground sm:text-[25px]">
          {score}
        </p>
        <p className="mt-1 text-[11px] font-medium leading-4 text-[#62748E] sm:text-xs">{label}</p>
        <div className="mt-auto h-1 overflow-hidden rounded-full bg-[#C3C3C3]">
          <div
            className="h-full rounded-full bg-black transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    </article>
  );
}

function PillarsGrid({ pillars, columns = 5 }) {
  const gridClass =
    columns <= 3
      ? 'mt-8 grid w-full grid-cols-3 gap-1'
      : columns === 4
        ? 'mt-8 grid w-full grid-cols-4 gap-1'
        : 'mt-8 grid w-full grid-cols-5 gap-1';

  return (
    <div className={gridClass} role="region" aria-label="Результаты по зонам">
      {pillars.map((pillar) => (
        <PillarCard key={pillar.id} {...pillar} />
      ))}
    </div>
  );
}

/** Нижний блок: CTA, логотип, кубики (по макету 2026-06-04). */
function ResultBottomSection({
  ctaHeading,
  ctaSubtext,
  ctaLabel,
  onCtaClick,
  consultationLoading,
  consultationSent,
  consultationSuccess,
  captcha,
  captchaReady = true,
}) {
  return (
    <section className="relative mt-10 overflow-hidden pb-2" aria-label="Действия и брендинг">
      {ctaHeading && (
        <h2 className="font-even-mono text-xl font-medium uppercase leading-tight tracking-[-0.35px] text-black">
          <span className="border-b-2 border-[#2B7AFE] pb-0.5">{ctaHeading}</span>
        </h2>
      )}
      {ctaSubtext && (
        <p className="mt-3 max-w-[480px] text-sm font-normal leading-5 tracking-[-0.15px] text-[#62748E]">
          {ctaSubtext}
        </p>
      )}

      {consultationSent ? (
        <div className="relative z-10 mt-6 max-w-[387px] rounded-lg border border-green-200 bg-green-50 p-4 text-green-800">
          <p className="font-medium">{consultationSuccess?.title || 'Результаты отправлены'}</p>
          <p className="mt-1 text-sm">
            {consultationSuccess?.text ||
              'Результаты отправлены на почту администратору.'}
          </p>
        </div>
      ) : (
        <>
          {captcha ? <div className="relative z-10 mt-6 max-w-[387px]">{captcha}</div> : null}
          <Button
            type="button"
            className="relative z-10 mt-6 flex h-12 w-full max-w-[387px] items-center justify-center gap-2 rounded-lg bg-black px-4 text-base font-medium tracking-[-0.31px] text-white hover:bg-black/90"
            onClick={onCtaClick}
            disabled={consultationLoading || (Boolean(captcha) && !captchaReady)}
          >
            <span>{consultationLoading ? 'Подготовка...' : ctaLabel}</span>
            <ArrowRight className="h-4 w-4 shrink-0" aria-hidden />
          </Button>
        </>
      )}

      <div className="relative mt-10 flex min-h-[148px] items-end justify-between gap-6">
        <div className="relative z-10 min-w-0 flex-1 pb-1">
          <img
            src={ASSETS.logo}
            alt="Антология"
            crossOrigin="anonymous"
            className="h-8 w-auto max-w-[200px] object-contain object-left"
            width={200}
            height={32}
          />
          <p className="mt-2 text-sm font-normal leading-5 tracking-[-0.15px] text-[#62748E]">
            Выгодный и удобный
            <br />
            подбор ИТ‑инструментов
          </p>
        </div>

        <img
          src={ASSETS.cubesGroup}
          alt=""
          crossOrigin="anonymous"
          className="pointer-events-none h-[148px] w-auto max-w-[min(46%,238px)] shrink-0 object-contain object-right-bottom"
          width={238}
          height={193}
          aria-hidden
        />
      </div>
    </section>
  );
}

/**
 * Финальный экран результатов чек-листа (вёрстка по Figma).
 */
const ChecklistResultScreen = ({
  data = CHECKLIST_RESULT_MOCK,
  onCtaClick,
  consultationLoading = false,
  consultationSent = false,
  consultationSuccess = null,
  captcha = null,
  captchaReady = true,
}) => {
  const d = data;

  return (
    <article
      className="relative mx-auto w-full max-w-[672px] overflow-hidden rounded-[14px] border border-[rgba(29,41,61,0.5)] bg-[#ECEBF2] shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)]"
      data-testid="checklist-result-screen"
    >
      <div
        className="absolute bottom-0 left-6 right-6 z-20 h-[4px] bg-gradient-to-r from-[#EC5A10] via-[#EEA002] to-[#FFBE2A]"
        aria-hidden
      />

      <div className="relative z-10 px-8 pb-10 pt-10">
        <div>
          <h1 className="font-even-mono text-[40px] leading-9 tracking-[-0.35px] text-black">
            {d.title}
          </h1>
          <p className="mt-[7px] text-xl font-medium leading-[1.2] tracking-normal text-black/50">
            {d.subtitle}
          </p>
        </div>

        <div
          className="relative mt-8 flex w-full max-w-[360px] items-center justify-center overflow-hidden rounded-2xl px-10 py-10 shadow-inner"
          style={{
            background: [
              'radial-gradient(ellipse 85% 55% at 18% 12%, rgba(255, 191, 42, 0.95) 0%, transparent 55%)',
              'radial-gradient(ellipse 75% 50% at 82% 88%, rgba(234, 56, 14, 0.85) 0%, transparent 50%)',
              'linear-gradient(90deg, #EA380E 0%, #F08517 42%, #FFBF2A 100%)',
            ].join(', '),
          }}
        >
          <p className="font-even-mono relative z-[1] flex items-baseline justify-center gap-1.5 text-[64px] tabular-nums leading-none text-white drop-shadow-sm sm:text-[72px]">
            <span>{d.score}</span>
            <span className="flex items-baseline gap-1 text-white/50">
              <span className="text-[0.55em] leading-none">/</span>
              <span>{d.maxScore}</span>
            </span>
          </p>
        </div>

        <div className="mt-6">
          <ReadinessBadge
            label={d.readinessLabel}
            emoji={d.readinessEmoji}
            readyLabels={d.readyLabels}
          />
        </div>

        <p className="mt-6 max-w-[576px] text-xl font-medium leading-[1.2] text-black/50">
          {d.summary}
        </p>

        <div className="mt-10">
          <div className="h-3 overflow-hidden rounded-full bg-[#D9D9D9]">
            <div
              className="h-full rounded-full bg-[#030213] transition-all"
              style={{ width: `${d.progressPercent}%` }}
              role="progressbar"
              aria-valuenow={d.progressPercent}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Уровень готовности"
            />
          </div>
          <div className="mt-2 flex justify-between gap-1 text-sm font-medium tracking-[-0.15px] text-black/80">
            {d.progressLabels.map((label) => (
              <span key={label} className="whitespace-nowrap">
                {label}
              </span>
            ))}
          </div>
        </div>

        <PillarsGrid pillars={d.pillars} columns={d.pillarColumns ?? d.pillars?.length ?? 5} />

        <ResultBottomSection
          ctaHeading={d.ctaHeading}
          ctaSubtext={d.ctaSubtext}
          ctaLabel={d.ctaLabel}
          onCtaClick={onCtaClick}
          consultationLoading={consultationLoading}
          consultationSent={consultationSent}
          consultationSuccess={consultationSuccess}
          captcha={captcha}
          captchaReady={captchaReady}
        />
      </div>
    </article>
  );
};

export default ChecklistResultScreen;
